import { apiClient } from '@/services/api-client';
import type {
  CheckoutRequiredMeta,
  CustomerCurrentSubscription,
  CustomerDowngradeEligibility,
  CustomerDowngradeEvaluation,
  CustomerUpgradeEligibility,
  CustomerUpgradeEvaluation,
  CustomerPlan,
  CustomerPlanDisplayStatus,
  CustomerPlanLimit,
  CustomerPlanManagementResponse,
  CustomerPlanPricingOption,
  CustomerTransitionEligibility,
  PaymentActionRequiredMeta,
} from '@/types/customer-plan-management';

type UnknownRecord = Record<string, unknown>;

type SubscriptionActionResponse = {
  message?: string;
  meta?: {
    active_until?: string;
    [key: string]: unknown;
  };
};

export interface CustomerSubscriptionActionResult {
  message: string | null;
  activeUntil: string | null;
}

export interface CustomerSubscriptionChangeRequest {
  planId: string;
  priceId?: string | null;
  cycleId?: string | null;
}

const readRecord = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as UnknownRecord) : {};

const readString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
};

const normalizeDisplayStatus = (value: unknown): CustomerPlanDisplayStatus => {
  const rawStatus = readString(value)?.toLowerCase();
  const status = rawStatus?.replace(/[\s-]+/g, '_');
  switch (status) {
    case 'pending_cancelation':
      return 'pending_cancellation';
    case 'canceled':
      return 'cancelled';
    case 'active':
    case 'trial':
    case 'trial_expired':
    case 'pending_cancellation':
    case 'cancelled':
    case 'past_due':
    case 'incomplete':
    case 'incomplete_expired':
    case 'unpaid':
    case 'paused':
      return status;
    default:
      return 'unknown';
  }
};

const normalizePricing = (input: unknown): CustomerPlanPricingOption[] => {
  const toCentsFromMajor = (value: number | null): number | null =>
    typeof value === 'number' ? Math.round(value * 100) : null;

  const normalizeEntry = (entry: unknown, fallbackId?: string): CustomerPlanPricingOption => {
    const record = readRecord(entry);
    const attributes = readRecord(record.attributes);
    const source = Object.keys(attributes).length ? attributes : record;
    const billingCycle = readRecord(source.billing_cycle ?? source.billingCycle);
    const cycle = readRecord(source.cycle);

    // Prefer explicit cents fields first.
    const centsFromExplicit =
      readNumber(source.unit_amount ?? source.unitAmount) ??
      readNumber(source.amount_cents ?? source.amountCents) ??
      readNumber(source.price_cents ?? source.priceCents);

    // Plan management payloads typically expose major units as base_amount/recurring_amount.
    const majorAmount =
      readNumber(source.recurring_amount ?? source.recurringAmount) ??
      readNumber(source.base_amount ?? source.baseAmount) ??
      readNumber(source.unit_price ?? source.unitPrice);

    // As a fallback, attempt to interpret generic price/amount. Heuristic:
    // treat >= 1000 as cents (e.g. 3900), otherwise assume major units (e.g. 39.00).
    const genericAmount = readNumber(source.price) ?? readNumber(source.amount);
    const centsFromGeneric =
      typeof genericAmount === 'number'
        ? Number.isInteger(genericAmount) && genericAmount >= 1000
          ? genericAmount
          : Math.round(genericAmount * 100)
        : null;

    const centsPrice = centsFromExplicit ?? toCentsFromMajor(majorAmount) ?? centsFromGeneric;

    return {
      id: readString(source.id ?? record.id) ?? fallbackId ?? '',
      planId: readString(source.plan_id ?? source.planId),
      priceId: readString(source.price_id ?? source.priceId) ?? readString(source.id ?? record.id),
      cycleId:
        readString(source.cycle_id ?? source.cycleId) ??
        readString(source.billing_cycle_id ?? source.billingCycleId) ??
        readString(billingCycle.id) ??
        readString(cycle.id),
      cycleName:
        readString(source.cycle_name ?? source.cycleName) ??
        readString(source.billing_cycle_name ?? source.billingCycleName) ??
        readString(billingCycle.name) ??
        readString(cycle.name),
      price: centsPrice,
      currency:
        readString(source.currency) ??
        readString(source.currency_code ?? source.currencyCode),
      formattedPrice:
        readString(source.formatted_price ?? source.formattedPrice) ??
        readString(source.display_price ?? source.displayPrice),
      interval:
        readString(source.interval) ??
        readString(source.billing_interval ?? source.billingInterval) ??
        readString(source.billing_cycle_name ?? source.billingCycleName) ??
        readString(source.cycle_name ?? source.cycleName),
      isCurrentSelection: readBoolean(
        source.is_current_selection ?? source.isCurrentSelection,
      ),
    };
  };

  if (Array.isArray(input)) {
    return input.map((entry) => normalizeEntry(entry));
  }

  const mapRecord = readRecord(input);
  return Object.entries(mapRecord).map(([key, value]) => normalizeEntry(value, key));
};

const normalizeLimits = (input: unknown): CustomerPlanLimit[] => {
  if (Array.isArray(input)) {
    return input.map((entry): CustomerPlanLimit => {
      const record = readRecord(entry);
      return {
        key: readString(record.key) ?? '',
        label: readString(record.label) ?? readString(record.key) ?? '',
        value: readNumber(record.value),
      };
    });
  }

  const record = readRecord(input);
  return Object.entries(record).map(([key, value]) => ({
    key,
    label: key.replace(/_/g, ' '),
    value: readNumber(value),
  }));
};

const normalizePlan = (entry: unknown): CustomerPlan => {
  const record = readRecord(entry);
  const attributes = readRecord(record.attributes);
  const source = Object.keys(attributes).length ? attributes : record;

  const featuresInput = source.features;
  const features = Array.isArray(featuresInput)
    ? featuresInput
        .map((item) => readString(item))
        .filter((item): item is string => Boolean(item))
    : [];

  return {
    id: readString(source.id ?? record.id) ?? '',
    name: readString(source.name) ?? '',
    tier: readString(source.tier),
    description: readString(source.description),
    isCurrent: readBoolean(source.is_current ?? source.isCurrent),
    features,
    limits: normalizeLimits(source.limits),
    includedSeats: readNumber(source.included_seats ?? source.includedSeats),
    maxSeats: readNumber(source.max_seats ?? source.maxSeats),
    pricing: normalizePricing(source.pricing ?? source.prices ?? source.pricesArray),
  };
};

const normalizeCurrentSubscription = (input: unknown): CustomerCurrentSubscription | null => {
  if (!input) return null;
  const record = readRecord(input);
  if (!Object.keys(record).length) return null;
  const features = Array.isArray(record.features)
    ? record.features
        .map((item) => readString(item))
        .filter((item): item is string => Boolean(item))
    : [];

  const rawStatus = record.display_status ?? record.displayStatus ?? record.status;

  return {
    id: readString(record.id),
    planId: readString(record.plan_id ?? record.planId),
    planName: readString(record.plan_name ?? record.planName),
    planTier: readString(record.plan_tier ?? record.planTier),
    cycleId: readString(record.cycle_id ?? record.cycleId),
    priceId: readString(record.price_id ?? record.priceId),
    displayStatus: normalizeDisplayStatus(rawStatus),
    statusLabel: readString(record.status_label ?? record.statusLabel) ?? normalizeDisplayStatus(rawStatus),
    nextBillingAt: readString(record.next_billing_at ?? record.nextBillingAt),
    trialExpiresAt: readString(
      record.trial_ends_at ?? record.trialEndsAt ?? record.trial_expires_at ?? record.trialExpiresAt,
    ),
    pendingCancellationEffectiveAt: readString(
      record.pending_cancellation_effective_at ?? record.pendingCancellationEffectiveAt,
    ),
    seatLimit: readNumber(record.seat_limit ?? record.seatLimit),
    seatUsage: readNumber(record.seat_usage ?? record.seatUsage),
    trialExpired: readBoolean(record.trial_expired ?? record.trialExpired),
    canPayNow: readBoolean(record.can_pay_now ?? record.canPayNow),
    features,
  };
};

const normalizeUpgradeEvaluations = (input: unknown): CustomerUpgradeEvaluation[] => {
  if (!Array.isArray(input)) return [];

  return input.map((entry) => {
    const record = readRecord(entry);
    const eligibility = readRecord(record.eligibility);
    const reasonsInput = eligibility.reason_codes ?? record.reason_codes;
    const reasons = Array.isArray(reasonsInput)
      ? reasonsInput
          .map((item) => readString(item))
          .filter((item): item is string => Boolean(item))
      : [];

    return {
      planId: readString(record.plan_id ?? record.planId) ?? '',
      eligible: readBoolean(eligibility.eligible ?? record.eligible),
      reasonCodes: reasons,
    };
  });
};

const normalizeUpgrade = (input: unknown): CustomerUpgradeEligibility => {
  const record = readRecord(input);
  // Backend may return options as string[] (plan IDs) or as objects like [{ id: "plan_..." }].
  const options = Array.isArray(record.options)
    ? record.options
        .map((item) => {
          if (typeof item === 'string') return readString(item);
          const optionRecord = readRecord(item);
          return readString(optionRecord.id ?? optionRecord.plan_id ?? optionRecord.planId);
        })
        .filter((item): item is string => Boolean(item))
    : [];

  return {
    currentPlanId: readString(record.current_plan_id ?? record.currentPlanId),
    currentPlanName: readString(record.current_plan_name ?? record.currentPlanName),
    options,
    evaluations: normalizeUpgradeEvaluations(record.evaluations),
    blockedReason: readString(record.blocked_reason ?? record.blockedReason),
    blockedReasonCode: readString(record.blocked_reason_code ?? record.blockedReasonCode),
    requiredAction: readString(record.required_action ?? record.requiredAction),
    resumeEndpoint: readString(record.resume_endpoint ?? record.resumeEndpoint),
    checkoutContextEndpoint: readString(
      record.checkout_context_endpoint ?? record.checkoutContextEndpoint,
    ),
    checkoutIntentEndpoint: readString(record.checkout_intent_endpoint ?? record.checkoutIntentEndpoint),
  };
};

const normalizeDowngradeEvaluations = (input: unknown): CustomerDowngradeEvaluation[] => {
  if (!Array.isArray(input)) return [];

  return input.map((entry) => {
    const record = readRecord(entry);
    const eligibility = readRecord(record.eligibility);
    const reasonsInput = eligibility.reason_codes ?? record.reason_codes;
    const reasons = Array.isArray(reasonsInput)
      ? reasonsInput
          .map((item) => readString(item))
          .filter((item): item is string => Boolean(item))
      : [];

    return {
      planId: readString(record.plan_id ?? record.planId) ?? '',
      eligible: readBoolean(eligibility.eligible ?? record.eligible),
      reasonCodes: reasons,
      seatUsage: readNumber(eligibility.seat_usage ?? eligibility.seatUsage),
      seatLimit: readNumber(eligibility.seat_limit ?? eligibility.seatLimit),
      quotaExceededMetrics: (() => {
        const metricsInput = (eligibility.quota_exceeded_metrics ??
          eligibility.quotaExceededMetrics) as unknown;
        if (!Array.isArray(metricsInput)) {
          return [];
        }
        return metricsInput
          .map((item) => readString(item))
          .filter((item): item is string => Boolean(item));
      })(),
    };
  });
};

const normalizeDowngrade = (input: unknown): CustomerDowngradeEligibility => {
  const record = readRecord(input);
  // Backend may return options as string[] (plan IDs) or as objects like [{ id: "plan_..." }].
  const options = Array.isArray(record.options)
    ? record.options
        .map((item) => {
          if (typeof item === 'string') return readString(item);
          const optionRecord = readRecord(item);
          return readString(optionRecord.id ?? optionRecord.plan_id ?? optionRecord.planId);
        })
        .filter((item): item is string => Boolean(item))
    : [];

  return {
    options,
    evaluations: normalizeDowngradeEvaluations(record.evaluations),
    blockedReason: readString(record.blocked_reason ?? record.blockedReason),
    blockedReasonCode: readString(record.blocked_reason_code ?? record.blockedReasonCode),
    requiredAction: readString(record.required_action ?? record.requiredAction),
    resumeEndpoint: readString(record.resume_endpoint ?? record.resumeEndpoint),
    checkoutContextEndpoint: readString(
      record.checkout_context_endpoint ?? record.checkoutContextEndpoint,
    ),
    checkoutIntentEndpoint: readString(record.checkout_intent_endpoint ?? record.checkoutIntentEndpoint),
  };
};

const normalizeTransitionEligibility = (input: unknown): CustomerTransitionEligibility => {
  const record = readRecord(input);
  return {
    upgrade: normalizeUpgrade(record.upgrade),
    downgrade: normalizeDowngrade(record.downgrade),
  };
};

const normalizePlanManagementResponse = (input: unknown): CustomerPlanManagementResponse => {
  const root = readRecord(input);
  const dataRecord = readRecord(root.data);
  const attributes = readRecord(dataRecord.attributes);
  const source = Object.keys(attributes).length
    ? attributes
    : Object.keys(dataRecord).length
      ? dataRecord
      : root;

  const plansInput = Array.isArray(source.plans) ? source.plans : [];
  const flagsRecord = readRecord(source.flags);

  return {
    data: {
      currentSubscription: normalizeCurrentSubscription(source.current_subscription ?? source.currentSubscription),
      plans: plansInput.map((entry) => normalizePlan(entry)),
      flags: {
        isTeamPlan: readBoolean(flagsRecord.is_team_plan ?? flagsRecord.isTeamPlan),
        hasPendingChange: readBoolean(flagsRecord.has_pending_change ?? flagsRecord.hasPendingChange),
        canResume: readBoolean(flagsRecord.can_resume ?? flagsRecord.canResume),
        requiresReactivationCheckout: readBoolean(
          flagsRecord.requires_reactivation_checkout ?? flagsRecord.requiresReactivationCheckout,
        ),
      },
      transitionEligibility: normalizeTransitionEligibility(
        source.transition_eligibility ?? source.transitionEligibility,
      ),
    },
  };
};

const normalizeMutationResult = (input: unknown): CustomerSubscriptionActionResult => {
  const root = readRecord(input);
  const meta = readRecord(root.meta);

  return {
    message: readString(root.message),
    activeUntil: readString(meta.active_until ?? meta.activeUntil),
  };
};

export function extractCheckoutRequiredMeta(error: unknown): CheckoutRequiredMeta | null {
  const payload = readRecord((error as { response?: { data?: unknown } })?.response?.data);
  const meta = readRecord(payload.meta);
  const code = readString(meta.code);
  const requiredAction = readString(meta.required_action);

  // Backend can emit multiple deterministic codes; required_action is the real signal.
  if (!requiredAction || requiredAction !== 'complete_checkout') {
    return null;
  }

  return {
    code: code ?? 'checkout_required',
    required_action: requiredAction ?? undefined,
    subscription_status: readString(meta.subscription_status) ?? undefined,
    checkout_context_endpoint: readString(meta.checkout_context_endpoint) ?? undefined,
    checkout_intent_endpoint: readString(meta.checkout_intent_endpoint) ?? undefined,
  };
}

export function extractPaymentActionRequiredMeta(error: unknown): PaymentActionRequiredMeta | null {
  const payload = readRecord((error as { response?: { data?: unknown } })?.response?.data);
  const meta = readRecord(payload.meta);
  const requiredAction = readString(meta.required_action);

  if (!requiredAction || requiredAction !== 'confirm_payment') {
    return null;
  }

  const clientSecret = readString(meta.client_secret ?? meta.clientSecret);
  if (!clientSecret) {
    return null;
  }

  return {
    code: readString(meta.code) ?? 'payment_action_required',
    required_action: requiredAction ?? undefined,
    stripe_invoice_id: readString(meta.stripe_invoice_id) ?? undefined,
    stripe_payment_intent_id: readString(meta.stripe_payment_intent_id) ?? undefined,
    stripe_intent_status: readString(meta.stripe_intent_status) ?? undefined,
    client_secret: clientSecret,
  };
}

class PlanManagementService {
  async getPlanManagement(): Promise<CustomerPlanManagementResponse> {
    const response = await apiClient.get<unknown>('/api/v1/customer/plan-management');
    return normalizePlanManagementResponse(response.data);
  }

  async upgrade(
    request: CustomerSubscriptionChangeRequest,
    prorate = true,
  ): Promise<CustomerSubscriptionActionResult> {
    const response = await apiClient.post<SubscriptionActionResponse>('/api/v1/customer/subscription/upgrade', {
      plan_id: request.planId,
      price_id: request.priceId ?? undefined,
      billing_cycle_id: request.cycleId ?? undefined,
      prorate,
    });

    return normalizeMutationResult(response.data);
  }

  async downgrade(
    request: CustomerSubscriptionChangeRequest,
    replacePending = false,
  ): Promise<CustomerSubscriptionActionResult> {
    const payload: Record<string, unknown> = {
      plan_id: request.planId,
      price_id: request.priceId ?? undefined,
      billing_cycle_id: request.cycleId ?? undefined,
    };
    // Avoid sending extra fields when not needed; backend clears pending cancellations automatically.
    if (replacePending) {
      payload.replace_pending = true;
    }
    const response = await apiClient.post<SubscriptionActionResponse>(
      '/api/v1/customer/subscription/downgrade',
      payload,
    );

    return normalizeMutationResult(response.data);
  }

  async cancel(): Promise<CustomerSubscriptionActionResult> {
    const response = await apiClient.post<SubscriptionActionResponse>('/api/v1/customer/subscription/cancel');
    return normalizeMutationResult(response.data);
  }

  async resume(): Promise<CustomerSubscriptionActionResult> {
    const response = await apiClient.post<SubscriptionActionResponse>('/api/v1/customer/subscription/resume');
    return normalizeMutationResult(response.data);
  }

  async getDowngradeOptions(): Promise<CustomerDowngradeEligibility> {
    const response = await apiClient.get<unknown>('/api/v1/customer/subscription/downgrade-options');
    const payload = readRecord(response.data);
    const source = readRecord(payload.data);
    return normalizeDowngrade(source);
  }
}

export const planManagementService = new PlanManagementService();
